import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowLeft, ExternalLink, BookOpen } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STORE_LABELS = {
  amazon: "Amazon",
  barnes: "Barnes & Noble",
  kobo: "Kobo",
  apple: "Apple Books",
  bookshop: "Bookshop.org",
};

export default function BookDetail() {
  const { slug } = useParams();
  const [book, setBook] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setBook(null);
    setNotFound(false);
    axios
      .get(`${API}/books/${slug}`)
      .then((r) => setBook(r.data))
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <div className="page-wrapper pt-40 pb-32 px-6 text-center">
        <div className="overline mb-4">404</div>
        <h1 className="font-serif text-5xl text-[#F4F4F6] mb-6">This story has wandered off.</h1>
        <Link to="/books" className="btn-primary">Back to the Library</Link>
      </div>
    );
  }

  if (!book) {
    return (
      <div className="page-wrapper pt-40 pb-32 px-6 text-center">
        <div className="overline animate-pulse">Loading…</div>
      </div>
    );
  }

  return (
    <div className="page-wrapper pt-32 md:pt-36" data-testid="book-detail-page">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage: `url(${book.cover_image})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            filter: "blur(60px)",
          }}
        />
        <div className="absolute inset-0 bg-[#1A1D24]/85" />

        <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24">
          <Link to="/books" data-testid="back-to-books" className="inline-flex items-center gap-2 text-sm text-[#F4F4F6]/60 hover:text-[#D4AF37] transition-colors mb-12">
            <ArrowLeft size={14} /> All Books
          </Link>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16 items-start">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="md:col-span-5"
            >
              <div className="aspect-[3/4] overflow-hidden glass-card relative">
                <img
                  src={book.cover_image}
                  alt={book.title}
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                    e.currentTarget.parentElement.classList.add("book-detail-cover-fallback");
                  }}
                  className="w-full h-full object-cover"
                />
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.15 }}
              className="md:col-span-7"
            >
              {book.series && (
                <div className="overline mb-4">
                  {book.series}
                  {book.series_order && ` · Book ${book.series_order}`}
                </div>
              )}
              <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl text-[#F4F4F6] font-light leading-[1.02] mb-6">
                {book.title}
              </h1>
              <p className="font-serif italic text-2xl text-[#D4AF37] leading-snug mb-8">
                "{book.tagline}"
              </p>
              <p className="text-[#A0A4AE] text-base md:text-lg leading-relaxed mb-10 whitespace-pre-line">
                {book.description}
              </p>

              <div className="grid grid-cols-3 gap-6 mb-10 pb-10 border-b border-[#D4AF37]/10">
                <div>
                  <div className="overline mb-2">Genre</div>
                  <div className="text-[#F4F4F6] text-sm">{book.genre}</div>
                </div>
                <div>
                  <div className="overline mb-2">Pages</div>
                  <div className="text-[#F4F4F6] text-sm">{book.pages || "—"}</div>
                </div>
                <div>
                  <div className="overline mb-2">Released</div>
                  <div className="text-[#F4F4F6] text-sm">{book.release_date}</div>
                </div>
              </div>

              {Object.keys(book.buy_links || {}).length > 0 ? (
                <>
                  <div className="overline mb-4">Available Wherever Books Are Sold</div>
                  <div className="flex flex-wrap gap-3">
                    {Object.entries(book.buy_links).map(([store, href]) => (
                      <a
                        key={store}
                        href={href}
                        data-testid={`buy-link-${store}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn-primary"
                      >
                        {STORE_LABELS[store] || store} <ExternalLink size={12} />
                      </a>
                    ))}
                  </div>
                </>
              ) : (
                <div className="inline-flex items-center gap-3 px-5 py-3 border border-[#D4AF37]/40 bg-[#D4AF37]/5">
                  <BookOpen size={16} className="text-[#D4AF37]" />
                  <span className="font-mono text-xs uppercase tracking-widest text-[#D4AF37]">Coming Soon — Pre-orders Open Q2</span>
                </div>
              )}
            </motion.div>
          </div>
        </div>
      </section>

      {/* EXCERPT */}
      {book.excerpt && (
        <section className="py-24 md:py-32 border-t border-[#D4AF37]/5">
          <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
            <div className="overline mb-8">A First Line</div>
            <motion.blockquote
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9 }}
              className="font-serif italic text-3xl sm:text-4xl text-[#F4F4F6] font-light leading-snug"
            >
              "{book.excerpt}"
            </motion.blockquote>
          </div>
        </section>
      )}
    </div>
  );
}
